import React, { useEffect, useState } from "react";
import axios from "axios";
import { backend_url } from "../App";
import "./CSS/Settings.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAdminId } from "../utils/roleUtils";

const initForm = {
    storeName: "",
    supportEmail: "",
    supportPhone: "",
    address: "",
    gstNumber: "",
    taxRate: 5,
    shippingCost: 0,
    freeShippingThreshold: 0,
    maintenanceMode: false,
    announcementText: "",
    announcementEnabled: false,
    theme: {
        primaryColor: "#7c3aed",
        secondaryColor: "#4f46e5",
        accentColor: "#B99CE2",
        backgroundColor: "#ffffff",
    },
};

const DEFAULT_THEME = initForm.theme;

// Tested combinations that are safe to fall back to if a custom pick looks off,
// grouped by color family so it's easy to browse variations of the same hue.
const PRESET_THEMES = [
    { family: "Purple", name: "Purple", primaryColor: "#7c3aed", secondaryColor: "#4f46e5", accentColor: "#B99CE2", backgroundColor: "#ffffff" },
    { family: "Purple", name: "Berry", primaryColor: "#a21caf", secondaryColor: "#c026d3", accentColor: "#f0abfc", backgroundColor: "#fdf4ff" },
    { family: "Purple", name: "Amethyst", primaryColor: "#7e22ce", secondaryColor: "#a855f7", accentColor: "#e9d5ff", backgroundColor: "#faf5ff" },

    { family: "Blue", name: "Ocean", primaryColor: "#2563eb", secondaryColor: "#0ea5e9", accentColor: "#93c5fd", backgroundColor: "#ffffff" },
    { family: "Blue", name: "Midnight", primaryColor: "#1e3a8a", secondaryColor: "#4338ca", accentColor: "#a5b4fc", backgroundColor: "#ffffff" },
    { family: "Blue", name: "Sky", primaryColor: "#0284c7", secondaryColor: "#38bdf8", accentColor: "#bae6fd", backgroundColor: "#f0f9ff" },

    { family: "Teal & Green", name: "Emerald", primaryColor: "#059669", secondaryColor: "#10b981", accentColor: "#6ee7b7", backgroundColor: "#ffffff" },
    { family: "Teal & Green", name: "Forest", primaryColor: "#166534", secondaryColor: "#16a34a", accentColor: "#86efac", backgroundColor: "#f7fdf9" },
    { family: "Teal & Green", name: "Lagoon", primaryColor: "#0d9488", secondaryColor: "#06b6d4", accentColor: "#67e8f9", backgroundColor: "#ffffff" },

    { family: "Red & Pink", name: "Rose", primaryColor: "#e11d48", secondaryColor: "#f43f5e", accentColor: "#fda4af", backgroundColor: "#ffffff" },
    { family: "Red & Pink", name: "Wine", primaryColor: "#7f1d1d", secondaryColor: "#b91c1c", accentColor: "#fca5a5", backgroundColor: "#fffafa" },
    { family: "Red & Pink", name: "Candy", primaryColor: "#db2777", secondaryColor: "#9333ea", accentColor: "#f9a8d4", backgroundColor: "#fff5fb" },

    { family: "Warm", name: "Amber", primaryColor: "#d97706", secondaryColor: "#f59e0b", accentColor: "#fcd34d", backgroundColor: "#ffffff" },
    { family: "Warm", name: "Sunset", primaryColor: "#ea580c", secondaryColor: "#f43f5e", accentColor: "#fdba74", backgroundColor: "#fffaf5" },
    { family: "Warm", name: "Gold Noir", primaryColor: "#1f2937", secondaryColor: "#b45309", accentColor: "#fbbf24", backgroundColor: "#ffffff" },

    { family: "Neutral", name: "Slate", primaryColor: "#334155", secondaryColor: "#475569", accentColor: "#94a3b8", backgroundColor: "#ffffff" },
    { family: "Neutral", name: "Charcoal", primaryColor: "#111827", secondaryColor: "#374151", accentColor: "#9ca3af", backgroundColor: "#ffffff" },
    { family: "Neutral", name: "Taupe", primaryColor: "#78716c", secondaryColor: "#a8a29e", accentColor: "#d6d3d1", backgroundColor: "#fdfcfb" },
];

const PRESET_FAMILIES = [...new Set(PRESET_THEMES.map((p) => p.family))];

// Flat single-color option per family — secondary equals primary, so gradients
// across the site render as one solid tone instead of a two-tone blend.
const SOLID_THEMES = [
    { family: "Purple", name: "Purple", primaryColor: "#7c3aed", secondaryColor: "#7c3aed", accentColor: "#B99CE2", backgroundColor: "#ffffff" },
    { family: "Blue", name: "Blue", primaryColor: "#2563eb", secondaryColor: "#2563eb", accentColor: "#93c5fd", backgroundColor: "#ffffff" },
    { family: "Teal & Green", name: "Green", primaryColor: "#059669", secondaryColor: "#059669", accentColor: "#6ee7b7", backgroundColor: "#ffffff" },
    { family: "Red & Pink", name: "Red", primaryColor: "#e11d48", secondaryColor: "#e11d48", accentColor: "#fda4af", backgroundColor: "#ffffff" },
    { family: "Warm", name: "Amber", primaryColor: "#d97706", secondaryColor: "#d97706", accentColor: "#fcd34d", backgroundColor: "#ffffff" },
    { family: "Neutral", name: "Slate", primaryColor: "#334155", secondaryColor: "#334155", accentColor: "#94a3b8", backgroundColor: "#ffffff" },
];

const Settings = () => {
    const [form, setForm] = useState(initForm);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [themeStyle, setThemeStyle] = useState("gradient");

    useEffect(() => { fetchSettings(); }, []);

    const fetchSettings = async () => {
        try {
            const res = await axios.get(`${backend_url}/api/settings`);
            if (res.data.success) {
                const fetched = res.data.settings;
                setForm({ ...initForm, ...fetched, theme: { ...DEFAULT_THEME, ...fetched.theme } });
            }
        } catch {
            toast.error("Failed to load settings");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const handleThemeChange = (field, value) =>
        setForm((prev) => ({ ...prev, theme: { ...prev.theme, [field]: value } }));

    const resetTheme = () => setForm((prev) => ({ ...prev, theme: { ...DEFAULT_THEME } }));

    const applyPreset = (preset) =>
        setForm((prev) => ({
            ...prev,
            theme: {
                primaryColor: preset.primaryColor,
                secondaryColor: preset.secondaryColor,
                accentColor: preset.accentColor,
                backgroundColor: preset.backgroundColor,
            },
        }));

    const isActivePreset = (preset) =>
        form.theme.primaryColor === preset.primaryColor &&
        form.theme.secondaryColor === preset.secondaryColor &&
        form.theme.accentColor === preset.accentColor &&
        form.theme.backgroundColor === preset.backgroundColor;

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await axios.put(`${backend_url}/api/settings`, {
                ...form,
                adminId: getAdminId(),
                adminName: sessionStorage.getItem("adminName"),
                adminRole: sessionStorage.getItem("adminRole"),
            });
            if (res.data.success) {
                toast.success("Settings saved successfully");
                const saved = res.data.settings;
                setForm({ ...initForm, ...saved, theme: { ...DEFAULT_THEME, ...saved.theme } });
            } else {
                toast.error("Failed to save settings");
            }
        } catch {
            toast.error("Failed to save settings");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="settings-page">
            <div className="settings-banner">
                <h1 className="settings-title">Store Settings</h1>
                <p className="settings-subtitle">Manage store info, tax and shipping configuration</p>
            </div>

            <div className="settings-content">
                {loading ? (
                    <div className="settings-loading">Loading settings...</div>
                ) : (
                    <form className="settings-form" onSubmit={handleSave}>

                        <div className="settings-card">
                            <h3>🏬 Store Information</h3>
                            <div className="settings-grid">
                                <div className="settings-field">
                                    <label>Store Name</label>
                                    <input
                                        value={form.storeName}
                                        onChange={(e) => handleChange("storeName", e.target.value)}
                                        placeholder="Aavryaa FabHub"
                                    />
                                </div>
                                <div className="settings-field">
                                    <label>Support Email</label>
                                    <input
                                        type="email"
                                        value={form.supportEmail}
                                        onChange={(e) => handleChange("supportEmail", e.target.value)}
                                        placeholder="support@aavryaa.com"
                                    />
                                </div>
                                <div className="settings-field">
                                    <label>Support Phone</label>
                                    <input
                                        value={form.supportPhone}
                                        onChange={(e) => handleChange("supportPhone", e.target.value)}
                                        placeholder="+91 98765 43210"
                                    />
                                </div>
                                <div className="settings-field">
                                    <label>GSTIN</label>
                                    <input
                                        value={form.gstNumber}
                                        onChange={(e) => handleChange("gstNumber", e.target.value)}
                                        placeholder="33AAAAA0000A1Z5"
                                    />
                                </div>
                                <div className="settings-field settings-field-wide">
                                    <label>Store Address</label>
                                    <textarea
                                        value={form.address}
                                        onChange={(e) => handleChange("address", e.target.value)}
                                        placeholder="Store pickup / registered address"
                                        rows={3}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="settings-card">
                            <h3>💰 Tax & Shipping</h3>
                            <div className="settings-grid">
                                <div className="settings-field">
                                    <label>Tax / GST Rate (%)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        value={form.taxRate}
                                        onChange={(e) => handleChange("taxRate", e.target.value)}
                                    />
                                </div>
                                <div className="settings-field">
                                    <label>Shipping Cost (₹)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={form.shippingCost}
                                        onChange={(e) => handleChange("shippingCost", e.target.value)}
                                    />
                                </div>
                                <div className="settings-field">
                                    <label>Free Shipping Above (₹)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={form.freeShippingThreshold}
                                        onChange={(e) => handleChange("freeShippingThreshold", e.target.value)}
                                        placeholder="0 = disabled"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="settings-card">
                            <h3>📢 Announcement Bar</h3>
                            <label className="settings-toggle-row">
                                <span>
                                    <strong>Show announcement on store</strong>
                                    <span className="settings-toggle-hint">Scrolling bar at the top of the customer website</span>
                                </span>
                                <input
                                    type="checkbox"
                                    checked={form.announcementEnabled}
                                    onChange={(e) => handleChange("announcementEnabled", e.target.checked)}
                                />
                            </label>
                            <div className="settings-field settings-field-wide" style={{ marginTop: 12 }}>
                                <label>Announcement Text</label>
                                <textarea
                                    value={form.announcementText}
                                    onChange={(e) => handleChange("announcementText", e.target.value)}
                                    placeholder="e.g. ✨ Festive Sale! Flat 15% OFF on all sarees till Sunday — use code FESTIVE15 🛍️"
                                    rows={2}
                                    maxLength={300}
                                />
                                <span className="settings-toggle-hint">
                                    {form.announcementText?.length || 0}/300 · Emojis are supported
                                </span>
                            </div>
                        </div>

                        <div className="settings-card">
                            <h3>🎨 Website Theme</h3>
                            <p className="settings-toggle-hint" style={{ marginBottom: 16 }}>
                                Choose the color palette used across the customer-facing website
                            </p>

                            <div className="theme-style-toggle">
                                <button
                                    type="button"
                                    className={themeStyle === "solid" ? "active" : ""}
                                    onClick={() => setThemeStyle("solid")}
                                >
                                    Solid Colors
                                </button>
                                <button
                                    type="button"
                                    className={themeStyle === "gradient" ? "active" : ""}
                                    onClick={() => setThemeStyle("gradient")}
                                >
                                    Gradients
                                </button>
                            </div>

                            {themeStyle === "solid" ? (
                                <div className="theme-presets-row" style={{ marginBottom: 4 }}>
                                    {SOLID_THEMES.map((preset) => (
                                        <button
                                            type="button"
                                            key={preset.name}
                                            onClick={() => applyPreset(preset)}
                                            className={`theme-preset-swatch${isActivePreset(preset) ? " active" : ""}`}
                                            style={{ "--swatch-color": preset.primaryColor }}
                                            title={preset.name}
                                        >
                                            <span className="theme-preset-dots">
                                                <span style={{ background: preset.primaryColor }} />
                                            </span>
                                            <span className="theme-preset-label">{preset.name}</span>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                PRESET_FAMILIES.map((family) => (
                                    <div className="theme-family-group" key={family}>
                                        <span className="theme-family-label">{family}</span>
                                        <div className="theme-presets-row">
                                            {PRESET_THEMES.filter((p) => p.family === family).map((preset) => (
                                                <button
                                                    type="button"
                                                    key={preset.name}
                                                    onClick={() => applyPreset(preset)}
                                                    className={`theme-preset-swatch${isActivePreset(preset) ? " active" : ""}`}
                                                    style={{ "--swatch-color": preset.primaryColor }}
                                                    title={preset.name}
                                                >
                                                    <span className="theme-preset-dots">
                                                        <span style={{ background: preset.primaryColor }} />
                                                        <span style={{ background: preset.secondaryColor }} />
                                                        <span style={{ background: preset.accentColor }} />
                                                    </span>
                                                    <span className="theme-preset-label">{preset.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                            <p className="settings-toggle-hint" style={{ margin: "10px 0 18px" }}>
                                Tested combinations — a safe fallback if a custom mix looks off. Pick one, or fine-tune the colors below.
                            </p>

                            <div className="theme-grid">
                                {[
                                    { key: "primaryColor", label: "Primary" },
                                    { key: "secondaryColor", label: "Secondary" },
                                    { key: "accentColor", label: "Accent" },
                                    { key: "backgroundColor", label: "Background" },
                                ].map(({ key, label }) => (
                                    <div className="theme-field" key={key}>
                                        <label>{label}</label>
                                        <div className="theme-swatch-row">
                                            <input
                                                type="color"
                                                value={form.theme[key]}
                                                onChange={(e) => handleThemeChange(key, e.target.value)}
                                                className="theme-color-input"
                                            />
                                            <input
                                                type="text"
                                                value={form.theme[key]}
                                                onChange={(e) => handleThemeChange(key, e.target.value)}
                                                className="theme-hex-input"
                                                maxLength={7}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Same rules as the website (aavryaa_frontend utils/theme.js):
                                buttons blend Primary → Secondary; pages use Background, or a
                                light Primary tint when Background is white */}
                            {(() => {
                                const t = form.theme;
                                const isWhite = /^#?f{3}(f{3})?$/i.test(String(t.backgroundColor).trim());
                                const surface = isWhite ? `color-mix(in srgb, ${t.primaryColor} 7%, #ffffff)` : t.backgroundColor;
                                return (
                                    <>
                                        <div className="theme-preview" style={{ background: t.backgroundColor }}>
                                            <div className="theme-preview-nav" style={{ background: t.primaryColor }}>
                                                <span>Aavryaa FabHub</span>
                                            </div>
                                            <div className="theme-preview-body"
                                                style={{ background: `linear-gradient(180deg, ${surface} 0%, ${t.backgroundColor} 100%)` }}>
                                                <button
                                                    type="button"
                                                    className="theme-preview-btn"
                                                    style={{ background: `linear-gradient(90deg, ${t.primaryColor}, ${t.secondaryColor})` }}
                                                >
                                                    Buy Now
                                                </button>
                                                <span className="theme-preview-tag"
                                                    style={{ background: t.accentColor, color: t.primaryColor }}>
                                                    New Arrival
                                                </span>
                                            </div>
                                        </div>
                                        <ul className="theme-legend">
                                            <li><i style={{ background: t.primaryColor }} /><b>Primary</b> — top bar, menu, links, icons, badges</li>
                                            <li><i style={{ background: `linear-gradient(90deg, ${t.primaryColor}, ${t.secondaryColor})` }} /><b>Secondary</b> — second colour of buttons (Buy Now, Add to Cart…)</li>
                                            <li><i style={{ background: t.accentColor }} /><b>Accent</b> — small highlights (home banner glow, search, tags)</li>
                                            <li><i style={{ background: t.backgroundColor }} /><b>Background</b> — plain page background</li>
                                        </ul>
                                    </>
                                );
                            })()}

                            <button type="button" className="theme-reset-btn" onClick={resetTheme}>
                                Reset to default palette
                            </button>
                        </div>

                        <div className="settings-card">
                            <h3>⚙️ Store Status</h3>
                            <label className="settings-toggle-row">
                                <span>
                                    <strong>Maintenance Mode</strong>
                                    <span className="settings-toggle-hint">Temporarily disable checkout / show maintenance banner</span>
                                </span>
                                <input
                                    type="checkbox"
                                    checked={form.maintenanceMode}
                                    onChange={(e) => handleChange("maintenanceMode", e.target.checked)}
                                />
                            </label>
                        </div>

                        <div className="settings-footer">
                            <button type="submit" className="settings-save-btn" disabled={saving}>
                                {saving ? "Saving..." : "Save Settings"}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            <ToastContainer />
        </div>
    );
};

export default Settings;
